import { Box, useMediaQuery, Grid } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/core/styles';
import {
  Page,
  useBaseStore,
  useMiscellaneousRoutes,
  useUserStore,
} from '@roc/feature-app-core';
import {
  ConversationAttribute,
  MyChatContainer,
  SelectConversation,
  useCommunicationRoutesConfig,
  useLoanCommunicationStore,
} from '@roc/feature-communication';
import {
  AllMessages,
  Documents,
  Overview,
  PendingDocuments,
  useDocumentCommunicationStore,
  useDocumentStore,
} from '@roc/feature-documents';
import {
  DrawDetails,
  DrawRequest,
  LoanDrawsDashboard,
  ReviseScopeOfWork,
} from '@roc/feature-draws';
import { useLoanRoutes } from '@roc/feature-loans-routes-config';
import {
  CLOSING,
  CLOSING_CONDITION_BANNER_MESSAGE,
  CLOSING_CONDITION_BANNER_MESSAGE_COMBINED_TASKS,
  getDocumentSection,
  getDrawId,
  getExtensionId,
  isFixAndFlip,
  isGroundUp,
  LoanProcess,
  LoanStatus,
  LoanType,
  SERVICING,
  UNDERWRITING,
} from '@roc/feature-utils';
import { DrawerMenu, IconButton, NotFound, PDFPreview } from '@roc/ui';
// Security review MED-004: validate the fast-track URL scheme before embedding it as an href.
import { isSafeUrl } from '@roc/ui/utils';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import 'react-perfect-scrollbar/dist/css/styles.css';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
} from 'react-router';
import { useLoanStore } from '../hooks/useLoanStore';
import BorrowerEntityOverview from './components/borrowerEntityOverview';
import { LoanDetailsLayout } from './components/loanDetailsLayout';
import { LoanDetailsMenu } from './components/loanDetailsMenu';
import Properties from './components/properties';
import { ExtensionDetails } from './modules/extensionDetails';
import { LoanContacts } from './modules/loanContacts/loanContacts';
import { ExtensionsDashboard } from './modules/extensionsDashboard';
import PayoffRequest from './payoffs/payoffRequest';
import { PayoffRequestV2 } from './payoffs/payoffRequestV2';
import { EstimatedClosingCosts } from './components/estimatedClosingCosts';
import { BannerMessages } from '../components/bannerMessages';
import { LoanDetailsPaperLayout } from '@roc/feature-loans-shared';
import { formatCurrency } from '@roc/ui/utils';
import {
  LoanSummaryCommissions,
  LoanSummaryExtended,
  LoanSummaryDecisionTab,
} from '@roc/feature-loan-summary';
import { MenuOpen } from '@material-ui/icons';
import {
  EditLoanBorrowers,
  EditLoanProperties,
  EditLoanTerms,
} from '@roc/feature-edit-loans';
import { LoanForeclosureRequest } from './foreclosure/components/loanForeclosureRequest';
import { LoanDetailsV2 } from './loanDetailsV2';
import { LoanDetailsV1 } from './loanDetailsV1';
import { NewUiBannerMessages } from '../components/newUiBannerMessages';
import { useGeneralTasksStore } from 'libs/feature-general-tasks/src/generalTasks/hooks/useGeneralTasksStore';
import { DownloadMobileAppDialog } from 'libs/feature-draws/src/draws/drawDetails/mobileSnapDrawAlert';
import { LoanTodoStore } from 'libs/feature-loans-shared/src/stores/loanTodoStore';
import { getConfirmLoanTermsBanner, getConfirmLoanTermsTextPending, getCurrentLoanTermValues } from '../utils/loanUtils';
import { formatPercentageValue } from 'libs/feature-one-tool/src/quote/utils/utils';
import { TodoStatus } from '@roc/feature-loan-details';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: '100%',
    },
    chatBox: {
      overflow: 'auto',
      [theme.breakpoints.down(769)]: {
        margin: '8px 0 0 0',
      },
    },
  })
);

const fastTrackElegibleLoanStatuses = [
  LoanStatus.UNKNOWN,
  LoanStatus.PENDING,
  LoanStatus.TERM_SHEET_OUT,
  LoanStatus.IN_UNDERWRITING,
  LoanStatus.PRESCREEN,
  LoanStatus.PENDING_DECISION,
  LoanStatus.PENDING_TERM_SHEET,
];

interface LoanDetailsProps {
  hideEditBorrowers?: boolean;
  renderLoanSummary: (props) => React.ReactNode;
}

export const LoanDetails = observer((props: LoanDetailsProps) => {
  const { globalStore } = useBaseStore();
  const classes = useStyles();
  const { renderLoanSummary } = props;
  const { loanId } = useParams<{ loanId: string }>();
  const { pathname } = useLocation();
  const { loanStore } = useLoanStore();
  const { documentStore } = useDocumentStore();
  const [showDownload, setShowDownload] = useState(false);
  const { documentCommunicationStore } = useDocumentCommunicationStore();
  const { loanCommunicationStore } = useLoanCommunicationStore();
  const {
    currentConversationSid,
    currentConversation,
    currentConversationIsLoanConversation,
  } = loanCommunicationStore;
  const { push } = useHistory();
  const {
    miscellaneousRoutes,
    miscellaneousRoutesConfig,
  } = useMiscellaneousRoutes();
  const { loanRoutesConfig } = useLoanRoutes();
  const twilioCommunicationEnabled =
    loanStore.loanDetails?.twilioCommunicationEnabled;
  const isNewSummaryScreenDesign = true;
  const theme = useTheme();
  const isTabletSize = useMediaQuery(theme.breakpoints.down(769));
  const isBridgeLoan =
    loanStore.loanDetails?.loanType == LoanType.RESIDENTIAL_BRIDGE;

  const { communicationRoutesConfig } = useCommunicationRoutesConfig();

  const documentSection = getDocumentSection(pathname);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuVisible, setMenuVisible] = useState(true);
  const [isLoanSummary, setIsLoanSummary] = useState(false);

  const [readyToShowLoanDetails, setReadyToShowLoanDetails] = useState(false);
  const { generalTasksStore } = useGeneralTasksStore();
  const { editTaskStore } = generalTasksStore;

  const toggleDrawerMenu = () => {
    setDrawerOpen(!drawerOpen);
  };

  const toggleMenu = () => {
    setMenuVisible(!menuVisible);
  };

  useEffect(() => {
    loanStore.resetLoanDetails();
    documentStore.reset();
    documentStore.loanTodosStore.reset();
    return () => {
      loanStore.resetLoanDetails();
      documentStore.reset();
      documentStore.loanTodosStore.reset();
    };
  }, [loanId]);

  useEffect(() => {
    if (!loanStore.isLoanDetailsSuccess) {
      push(miscellaneousRoutesConfig.notFound.url);
    }
  }, [loanStore.isLoanDetailsSuccess, documentStore]);

  useEffect(() => {
    if (
      !loanStore.loanDetails?.loanId ||
      documentCommunicationStore.isDrawerOpen
    )
      return;
    loanCommunicationStore.fetchUnreadMessagesCount(loanId);
    if (!loanStore.loanDetails?.twilioCommunicationEnabled) {
      documentStore.fetchUnreadComments(loanId);
    }
  }, [
    documentCommunicationStore.isDrawerOpen,
    loanStore.loanDetails?.loanId,
    documentStore,
  ]);

  useEffect(() => {
    if (!loanStore.loanDetails?.loanId || globalStore.userFeatures?.isServicer)
      return;
    documentStore.updateIsProposal(false);
    loanStore.fetchPreviousDraws(loanId);
    if (
      loanStore.loanDetails?.fastTrack &&
      !loanStore.loanDetails?.fastrackPaymentComplete
    ) {
      loanStore.fetchLoanFastTrackPaymentUrl(loanId);
    }
  }, [loanStore.loanDetails?.loanId, documentStore]);

  useEffect(() => {
    setReadyToShowLoanDetails(false);
    loanStore.fetchLoanDetails(loanId, () => {
      setReadyToShowLoanDetails(true);
    });
  }, [
    loanStore,
    loanStore.submitAppraisalSuccess,
    loanStore.borrowerUpdatedCounter,
    loanId,
  ]);

  useEffect(() => {
    loanStore.fetchLoanChangeRequests(Number(loanId))
    loanStore.fetchLoanExpirationDates(Number(loanId));
    loanStore.fetchLoanPrescreenData(Number(loanId));
    loanStore.fetchExceptionsAndCommentsExternal(
      loanId,
      'Underwriter Comments'
    );
  }, []);

  useEffect(() => {
    loanStore.fetchLoanClosingConditions(Number(loanId));
  }, [loanId]);

  useEffect(() => {
    setDrawerOpen(false);
    setMenuVisible(!pathname.includes('/loan-summary'));
    setIsLoanSummary(pathname.includes('/loan-summary'));
  }, [pathname]);

  const onUserNewConversation = currentConversationIsLoanConversation
    ? async inviteUserId => {
      const attributes = currentConversation?.attributes as ConversationAttribute;
      const response = await loanCommunicationStore.createNewUserConversation(
        attributes.loanId,
        [loanCommunicationStore.userId, inviteUserId]
      );
      if (currentConversationSid != response?.conversationSid) {
        window.location.href = communicationRoutesConfig
          .conversations(`${attributes.loanId}`)
          .children.conversation(response.conversationSid).url;
      }
    }
    : undefined;

  const onOpenAddTask = currentConversationIsLoanConversation ?
    async (assigneeId) => {
      const attributes = currentConversation?.attributes as ConversationAttribute;
      editTaskStore.openAddTask(attributes?.loanId, null, { assigneeId }, null);
    }
    : undefined;

  const renderDrawDetails = () => {
    const drawId = getDrawId(pathname);
    if (true && drawId) {
      return (
        <Route
          exact
          path={
            loanRoutesConfig.loans(loanId).children.drawDetails(drawId).path
          }
        >
          <Page
            routeDefinition={loanRoutesConfig
              .loans(loanId)
              .children.drawDetails(drawId)}
          >
            <DrawDetails
              loanId={loanId}
              drawId={drawId}
              adminSpecialist={
                loanStore.loanDetails?.drawAdminSpecialistFullName
              }
              reviewSpecialist={
                loanStore.loanDetails?.drawReviewSpecialistFullName
              }
            />
          </Page>
        </Route>
      );
    } else {
      return null;
    }
  };

  const renderPayoffScreen = (servicer, version) => {
    const showPayoffModule = [
      'SLS',
      'Keybank',
      'Fay Servicing',
      'Planet Home Lending',
      'Planet Portfolio',
    ].includes(servicer);
    return showPayoffModule ? (
      <PayoffRequestV2 loanId={loanId} showTitle={version == 'v2' ? false : true} style={{
        padding: version == 'v2' ? 0 : 16
      }} />
    ) : (
      <PayoffRequest />
    );
  };
  const renderExtensionDetails = () => {
    const extensionId = getExtensionId(pathname);
    if (true && extensionId) {
      return (
        <Route
          exact
          path={
            loanRoutesConfig
              .loans(loanId)
              .children.extensionDetails(extensionId).path
          }
        >
          <Page
            routeDefinition={loanRoutesConfig
              .loans(loanId)
              .children.extensionDetails(extensionId)}
          >
            <ExtensionDetails loanId={loanId} extensionId={extensionId} />
          </Page>
        </Route>
      );
    } else {
      return null;
    }
  };

  const renderFastTrackPaymentReminder = () => {
    return loanStore.loanDetails?.fastTrack &&
      !loanStore.loanDetails?.fastrackPaymentComplete &&
      loanStore.loanDetails?.status &&
      fastTrackElegibleLoanStatuses.includes(loanStore.loanDetails?.status) &&
      loanStore.loanFastTrackUrl ? (
      <BannerMessages
        newDesignForSummaryScreen={isNewSummaryScreenDesign}
        messages={[
          {
            title: 'Fast Track Payment is Still Pending',
            content: `Payment is required in order to initiate Fast Track process. <a id="fastTrackPaymentLink" href="${isSafeUrl(loanStore.loanFastTrackUrl) ? loanStore.loanFastTrackUrl : ''}" target="_blank" rel="noopener noreferrer"/>Click here</a> to pay now for the fast track fees. `,
          },
        ]}
      />
    ) : null;
  };


  const renderRepeatPropertyWarning = () => {
    const repeatedPropertyAddresses = loanStore.loanDetails?.properties
      ?.filter((property) => property.repeatedProperty)
      .map((property) => property.address);
    const { userStore } = useUserStore();
    let crmUser = userStore.isBackOfficeUser;
    if (crmUser) {
      if ((!repeatedPropertyAddresses || repeatedPropertyAddresses.length === 0)) {
        return null;
      }

      let messageContent = '';

      if (repeatedPropertyAddresses.length >= 1) {
        messageContent = 'There is another loan in the system reflecting this address. Please reach out to your RM for more information.';
      }

      return (
        <BannerMessages
          newDesignForSummaryScreen={isNewSummaryScreenDesign}
          messages={[
            {
              title: 'Duplicate Address Alert ',
              content: messageContent,
            },
          ]}
        />
      );
    }
    return null;
  };

  const confirmLoanTermMessage = () => {
    const todos = documentStore.loanTodosStore.todosByLoanProcess[LoanProcess.TODOS] || [];
    const underwritingTodos = documentStore.loanTodosStore.todosByLoanProcess[LoanProcess.UNDERWRITING] || [];
    const loanDetails = loanStore.loanDetails;
    const { loanSubType } = loanDetails;
    const isGUorFFLoan = isFixAndFlip(loanSubType) || isGroundUp(loanSubType);
    if (!isGUorFFLoan) {
      return null;
    }
    const allTodos = [...todos, ...underwritingTodos];
    const confirmLoanTermTodo = allTodos.find(todo => todo.title === 'Accept Loan Terms' && (todo.status === TodoStatus.PENDING));
    if (!confirmLoanTermTodo) {
      return null;
    }
    const currentTerms = getCurrentLoanTermValues(loanDetails);
    return (
      <BannerMessages
        newDesignForSummaryScreen={isNewSummaryScreenDesign}
        messages={[
          {
            title: 'Accept Loan Terms',
            content: getConfirmLoanTermsTextPending(currentTerms.initialLoanAmount, currentTerms.constructionHoldback, currentTerms.arltv, currentTerms.rate, currentTerms.interestReserveMonths)
          },
        ]}
      />
    );
  }

  const renderIVPilotProgramWarning = () => {
    const loanDetails = loanStore.loanDetails;
    const isLoanInIVPilotProgram = !!loanDetails.internalValuationPilotSelected;

    if (!isLoanInIVPilotProgram) {
      return null;
    }
    const content = () => <Grid container xs={12} style={{ marginBottom: 8 }}>
      <Box>
        Property Photos, SOW & Fee Required: We’ll get started on your valuation as soon as the Property Photos are taken, the Scope of Work is uploaded and the fee is paid. Complete them to avoid delays!.{' '}
        <span
          style={{ textDecoration: 'underline', cursor: 'pointer' }}
          onClick={() => setShowDownload(true)}
        >
          Download App here
        </span><br />
        Once the property photos, valuation fee, and scope of work are provided, the internal valuation team can provide a quick and accurate value.
      </Box>
      <DownloadMobileAppDialog
        open={showDownload}
        onClose={() => setShowDownload(false)}
        lenderId={loanStore.loanDetails?.lenderId}
      />
    </Grid>

    return (
      <BannerMessages
        newDesignForSummaryScreen={isNewSummaryScreenDesign}
        isBlueBanner={true}
        messages={[
          {
            title: 'This loan is part of the RealVal Program.',
            content: content(),
          },
        ]}
      />
    );
  }

  const renderManualQuoteWarning = () => {
    if (loanStore?.lastFetchedCustomQuote?.action !== 'Updated') {
      return null;
    }

    const content = () => <Grid container xs={12} style={{ marginBottom: 8 }}>
      <Box>
        {getConfirmLoanTermsBanner()}
      </Box>
    </Grid>

    return (
      <BannerMessages
        newDesignForSummaryScreen={isNewSummaryScreenDesign}
        isBlueBanner={false}
        messages={[
          {
            title: 'Accept Loan Terms',
            content: content(),
          },
        ]}
      />
    );
  }

  const renderLoanClosingCondition = () => {
    if (loanStore.loanClosingConditions != undefined) {
      // TODO: add document filters later
      const conditionsToDisplay = loanStore.loanClosingConditions.filter(item => item.status !== 'ACCEPTED' && item.status !== 'NOT_APPLICABLE');

      return conditionsToDisplay.length > 0 && globalStore.userFeatures?.closingConditionBannerAllowed ? (
        <NewUiBannerMessages
          newDesignForSummaryScreen={isNewSummaryScreenDesign}
          messages={[
            {
              title: loanStore.loanDetails?.combinedTaskFrameworkEnabled ? CLOSING_CONDITION_BANNER_MESSAGE : CLOSING_CONDITION_BANNER_MESSAGE_COMBINED_TASKS,
              content: `
                  <style>
                    .pending-closing-conditions-list {
                      list-style: none;
                      margin: 0;
                      padding-left: 0;
                      max-height: 100px;
                      overflow-y: hidden;
                      overflow-x: scroll !important;
                      column-width: 250px;
                      column-gap: 24px;
                      column-fill: auto;
                      scrollbar-width: thin !important;
                      scrollbar-color: #8a8a8a4e #f1f1f1 !important;
                    }
                    .pending-closing-conditions-list li {
                      break-inside: avoid;
                      -webkit-column-break-inside: avoid;
                    }
                    .pending-closing-conditions-list::-webkit-scrollbar {
                      height: 5px;
                    }
                    .pending-closing-conditions-list::-webkit-scrollbar-track {
                      background: #f1f1f1;
                      border-radius: 4px;
                    }
                    .pending-closing-conditions-list::-webkit-scrollbar-thumb {
                      background-color: #8a8a8a4e;
                      border-radius: 4px;
                    }
                  </style>
                  <ul class="pending-closing-conditions-list">
                  ${conditionsToDisplay.map(entry => `
                    <li key=${entry.taskId}>
                      <strong>${entry.title}</strong>
                    </li>`).join('')}
                  </ul>
              `,
            },
          ]}
        />
      ) : null;
    }
  };

  if (!readyToShowLoanDetails || !loanStore.loanDetails) {
    return <></>;
  }

  return (
    <LoanDetailsV2
      renderLoanSummary={props.renderLoanSummary}
      hideEditBorrowers={props.hideEditBorrowers}
      isLoanSummary={isLoanSummary as boolean}
      onUserNewConversation={onUserNewConversation}
      renderFastTrackPaymentReminder={renderFastTrackPaymentReminder}
      renderRepeatPropertyWarning={renderRepeatPropertyWarning}
      renderIVPilotProgramWarning={renderIVPilotProgramWarning}
      renderManualQuoteWarning={renderManualQuoteWarning}
      renderLoanClosingCondition={renderLoanClosingCondition}
      renderDrawDetails={renderDrawDetails}
      confirmLoanTermMessage={confirmLoanTermMessage}
      renderExtensionDetails={renderExtensionDetails}
      renderPayoffScreen={(servicer) => renderPayoffScreen(servicer, 'v2')}
      onOpenAddTask={onOpenAddTask}
    />
  );
});
